// src/pages/admin/CompanyAnalyses.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analysisService } from '../../services/analysisService';
import { analysisSharingService } from '../../services/analysisSharingService';
import { companyService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';
import {
  FolderIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ChartPieIcon,
  CalendarIcon,
  UserIcon,
  ClockIcon,
  ChevronDownIcon,
  EyeIcon,
  BuildingOfficeIcon,
  ShareIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const CompanyAnalyses = () => {
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === 'superadmin';

  // State
  const [activeTab, setActiveTab] = useState('analyses'); // 'analyses' or 'shares'
  const [analyses, setAnalyses] = useState([]);
  const [shares, setShares] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedAnalysisForShare, setSelectedAnalysisForShare] = useState(null);
  const [shareTargetCompany, setShareTargetCompany] = useState('');
  const [sharePermission, setSharePermission] = useState('read-only');
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analysesResponse, sharesRes, compRes] = await Promise.all([
        isSuperAdmin
          ? analysisService.getAnalyses({ limit: 1000 })
          : analysisService.getAnalyses(),
        isSuperAdmin ? analysisSharingService.getShares() : Promise.resolve({ data: [] }),
        isSuperAdmin ? companyService.getCompanies({ limit: 500 }) : Promise.resolve({ data: [] })
      ]);

      if (analysesResponse.success) setAnalyses(analysesResponse.data || []);
      else throw new Error(analysesResponse.message || 'Erreur lors du chargement des analyses');

      if (isSuperAdmin) {
        if (sharesRes.data) {
          setShares(sharesRes.data.data || sharesRes.data);
        }
        if (compRes.data) {
          setCompanies(compRes.data.data || compRes.data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showErrorToast('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleShareAnalysis = async (e) => {
    e.preventDefault();
    if (!shareTargetCompany) {
      showErrorToast('Veuillez sélectionner une entreprise cible');
      return;
    }
    
    setIsSharing(true);
    try {
      await analysisSharingService.createShare({
        analysisId: selectedAnalysisForShare.id || selectedAnalysisForShare._id,
        targetCompanyId: shareTargetCompany,
        permission: sharePermission
      });
      showSuccessToast('Analyse partagée avec succès');
      setShowShareModal(false);
      setShareTargetCompany('');
      setSharePermission('read-only');
      if (activeTab === 'shares') fetchData();
    } catch (error) {
      console.error('Error sharing analysis:', error);
      showErrorToast(error.response?.data?.message || 'Erreur lors du partage de l\'analyse');
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevokeShare = async (shareId) => {
    if (!window.confirm('Voulez-vous vraiment révoquer ce partage ?')) return;
    
    try {
      await analysisSharingService.deleteShare(shareId);
      showSuccessToast('Partage révoqué');
      fetchData();
    } catch (error) {
      console.error('Error revoking share:', error);
      showErrorToast('Erreur lors de la révocation du partage');
    }
  };

  const handleUpdateSharePermission = async (shareId, newPermission) => {
    try {
      await analysisSharingService.updateShare(shareId, { permission: newPermission });
      showSuccessToast('Permission mise à jour');
      fetchData();
    } catch (error) {
      console.error('Error updating share:', error);
      showErrorToast('Erreur lors de la mise à jour de la permission');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    return company ? company.name : 'Entreprise Inconnue';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { color: 'bg-gray-100 text-gray-800', text: 'Brouillon' },
      'in_progress': { color: 'bg-blue-100 text-blue-800', text: 'En cours' },
      'completed': { color: 'bg-green-100 text-green-800', text: 'Terminée' },
      'archived': { color: 'bg-yellow-100 text-yellow-800', text: 'Archivée' }
    };
    const config = statusConfig[status] || statusConfig['draft'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const filteredAnalyses = analyses.filter(analysis => {
    const companyName = getCompanyName(analysis.companyId);
    const matchesSearch = analysis.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         analysis.mission?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         analysis.createdByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || analysis.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading && analyses.length === 0 && shares.length === 0) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Analyses</h1>
          <p className="text-gray-600 mt-1">
            {isSuperAdmin ? "Vue globale et partage des analyses entre entreprises" : "Vue d'ensemble des analyses EBIOS RM de votre entreprise"}
          </p>
        </div>
      </div>

      {/* Tabs for SuperAdmin */}
      {isSuperAdmin && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('analyses')}
              className={`${
                activeTab === 'analyses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Toutes les analyses
            </button>
            <button
              onClick={() => setActiveTab('shares')}
              className={`${
                activeTab === 'shares'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <ShareIcon className="w-5 h-5 mr-2" />
              Partages actifs
            </button>
          </nav>
        </div>
      )}

      {activeTab === 'analyses' ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total</p>
                  <p className="text-2xl font-semibold text-gray-900">{analyses.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ClockIcon className="w-5 h-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">En cours</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {analyses.filter(a => a.status === 'in_progress').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Terminées</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {analyses.filter(a => a.status === 'completed').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <UserIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Brouillons</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {analyses.filter(a => a.status === 'draft').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Rechercher par nom, mission ou créateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="draft">Brouillons</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Terminées</option>
                  <option value="archived">Archivées</option>
                </select>
              </div>
            </div>
          </div>

          {/* Analyses Grid */}
          {filteredAnalyses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune analyse trouvée</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Essayez de modifier vos critères de recherche.' 
                  : 'Aucune analyse n\'a été créée pour cette entreprise.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAnalyses.map((analysis) => {
                const analysisId = analysis._id || analysis.id;
                // Find shares for this specific analysis
                const analysisShares = isSuperAdmin ? shares.filter(s => {
                  const shareAnalysisId = s.analysisId?._id || s.analysisId;
                  return shareAnalysisId === analysisId;
                }) : [];

                return (
                  <div key={analysisId} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 flex flex-col">
                    <div className="p-6 flex-grow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                            {analysis.name || 'Analyse sans nom'}
                          </h3>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {getStatusBadge(analysis.status)}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {/* Ownership Section */}
                        {isSuperAdmin && (
                          <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Propriétaire</p>
                            <div className="flex items-center text-sm font-medium text-gray-900">
                              <BuildingOfficeIcon className="w-4 h-4 mr-2 text-indigo-500" />
                              {getCompanyName(analysis.companyId)}
                            </div>
                          </div>
                        )}

                        {analysis.mission && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Mission</p>
                            <p className="text-sm text-gray-900 line-clamp-2">{analysis.mission}</p>
                          </div>
                        )}

                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <UserIcon className="w-4 h-4 mr-2" />
                            <span>{analysis.createdByName || 'Créateur inconnu'}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            <span>
                              {analysis.createdAt 
                                ? new Date(analysis.createdAt).toLocaleDateString('fr-FR')
                                : 'Date inconnue'}
                            </span>
                          </div>
                        </div>

                        {/* Sharing Information Section */}
                        {isSuperAdmin && analysisShares.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                              <ShareIcon className="w-3 h-3 mr-1" /> Partagée avec ({analysisShares.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {analysisShares.map(share => (
                                <div key={share._id} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 shadow-sm">
                                  <span className="truncate max-w-[120px]" title={share.targetCompanyId?.name}>
                                    {share.targetCompanyId?.name || 'Entreprise'}
                                  </span>
                                  <span className={`ml-1.5 w-2 h-2 rounded-full ${share.permission === 'read-write' ? 'bg-green-500' : 'bg-gray-400'}`} 
                                        title={share.permission === 'read-write' ? 'Lecture/Écriture' : 'Lecture Seule'}>
                                  </span>
                                </div>
                              ))}
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 italic">
                              Point vert: L/E, Point gris: L/S. Allez dans "Partages actifs" pour gérer.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between rounded-b-lg">
                      <div className="flex items-center space-x-2">
                        {analysis.workshopsCompleted && (
                          <span className="text-xs font-medium text-gray-600 bg-white border border-gray-200 px-2.5 py-1 rounded-md shadow-sm">
                            {analysis.workshopsCompleted}/5 Ateliers
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {isSuperAdmin && (
                          <button
                            onClick={() => {
                              setSelectedAnalysisForShare(analysis);
                              setShowShareModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors shadow-sm"
                            title="Partager cette analyse avec une autre entreprise"
                          >
                            <ShareIcon className="w-4 h-4 mr-1.5" />
                            Partager
                          </button>
                        )}
                        <button
                          onClick={() => {
                            localStorage.setItem('currentAnalysisId', analysisId);
                            window.dispatchEvent(new Event('storage'));
                            window.location.href = '/app';
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          <EyeIcon className="w-4 h-4 mr-1.5" />
                          Ouvrir
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        /* SHARES TAB */
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {shares.length === 0 ? (
             <div className="text-center py-12">
             <ShareIcon className="mx-auto h-12 w-12 text-gray-400" />
             <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun partage actif</h3>
             <p className="mt-1 text-sm text-gray-500">
               Vous n'avez pas encore partagé d'analyses entre entreprises.
             </p>
           </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Analyse</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise Cible</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shares.map(share => (
                    <tr key={share._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{share.analysisId?.name || 'Inconnue'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{share.sourceCompanyId?.name || 'Inconnue'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-semibold">{share.targetCompanyId?.name || 'Inconnue'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={share.permission}
                          onChange={(e) => handleUpdateSharePermission(share._id, e.target.value)}
                          className="text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="read-only">Lecture Seule</option>
                          <option value="read-write">Lecture & Écriture</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRevokeShare(share._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Révoquer l'accès"
                        >
                          <TrashIcon className="w-5 h-5 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowShareModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleShareAnalysis}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ShareIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1 min-w-0">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Partager l'analyse entre entreprises
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Analyse source</label>
                          <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 font-semibold truncate">
                            {selectedAnalysisForShare?.name} ({getCompanyName(selectedAnalysisForShare?.companyId)})
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Entreprise Cible</label>
                          <select
                            required
                            value={shareTargetCompany}
                            onChange={(e) => setShareTargetCompany(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          >
                            <option value="">Sélectionnez une entreprise...</option>
                            {companies
                              .filter(c => c._id !== selectedAnalysisForShare?.companyId) // Hide source company
                              .map(c => (
                              <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Type de permission</label>
                          <select
                            value={sharePermission}
                            onChange={(e) => setSharePermission(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          >
                            <option value="read-only">Lecture Seule (Vue uniquement)</option>
                            <option value="read-write">Lecture & Écriture (Modification autorisée)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSharing || !shareTargetCompany}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isSharing ? 'Partage en cours...' : 'Partager'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowShareModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CompanyAnalyses;